import { useEffect, useRef } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuAlertTriangle } from 'react-icons/lu';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import CustomDatePicker from '@Common/CustomDatePicker';
import Toggle from '../../../../components/Toggle';
import Input from '@Common/input';
import { FORM_REGEX_VALIDATORS } from '@utils';
import PlaceholderImage from '../../../channels/components/UnitsMapping/PlaceholderImage';
import type { IAvailableProperty } from '../../types';


interface ExtendWithMoveTabProps {
  extensionDate: string;
  onDateChange: (date: string) => void;
  availableProperties: IAvailableProperty[];
  isLoadingProperties: boolean;
  selectedPropertyId: number | null;
  onSelectProperty: (propertyId: number) => void;
  isRtl: boolean;
  propertyImage?: string;
  propertyName?: string;
  checkInDate?: string;
  checkOutDate?: string;
  platformSrc?: number;
  initialGuestEmail?: string;
  isDirectPaymentAccepted: boolean;
  onDirectPaymentChange: (checked: boolean) => void;
  guestEmail: string;
  onGuestEmailChange: (email: string) => void;
  onValidationChange: (isValid: boolean) => void;
}

interface IExtendWithMoveForm {
  extensionDate: string;
  guestEmail: string;
  isDirectPaymentAccepted: boolean;
}

const ExtendWithMoveTab = ({
  extensionDate,
  onDateChange,
  availableProperties,
  isLoadingProperties,
  selectedPropertyId,
  onSelectProperty,
  isRtl,
  propertyImage,
  propertyName,
  checkInDate,
  checkOutDate,
  platformSrc,
  initialGuestEmail,
  isDirectPaymentAccepted,
  onDirectPaymentChange,
  guestEmail,
  onGuestEmailChange,
  onValidationChange,
}: ExtendWithMoveTabProps) => {
  const { t } = useTranslation();
  const isInitialSync = useRef(true);

  const methods = useForm<IExtendWithMoveForm>({
    mode: 'onChange',
    defaultValues: {
      extensionDate: extensionDate || '',
      guestEmail: guestEmail || initialGuestEmail || '',
      isDirectPaymentAccepted: isDirectPaymentAccepted || false,
    },
  });

  useEffect(() => {
    onValidationChange(methods.formState.isValid);
  }, [methods.formState.isValid, onValidationChange]);

  // Sync parent value → local form (initial only)
  useEffect(() => {
    if (extensionDate && isInitialSync.current) {
      methods.reset({ extensionDate });
      isInitialSync.current = false;
    }
  }, [extensionDate, methods]);

  // Watch local form changes → propagate to parent
  useEffect(() => {
    const subscription = methods.watch((values) => {
      const date = values.extensionDate
        ? dayjs(values.extensionDate as any).isValid()
          ? dayjs(values.extensionDate as any).format('YYYY-MM-DD')
          : String(values.extensionDate)
        : '';
      if (date !== extensionDate) {
        onDateChange(date);
      }
      if (values.guestEmail !== guestEmail) {
        onGuestEmailChange(values.guestEmail || '');
      }
      if (values.isDirectPaymentAccepted !== isDirectPaymentAccepted) {
        onDirectPaymentChange(!!values.isDirectPaymentAccepted);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, onDateChange, extensionDate, guestEmail, onGuestEmailChange, isDirectPaymentAccepted, onDirectPaymentChange]);

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Current reservation info card */}
        {(propertyName || checkInDate) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              p: '12px',
              borderRadius: '8px',
              backgroundColor: '#F9F8FF',
              border: '1px solid #E8E4FF',
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '48px',
                borderRadius: '8px',
                backgroundColor: '#E8E8EA',
                flexShrink: 0,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {propertyImage ? (
                <Box
                  component="img"
                  src={propertyImage}
                  alt={propertyName}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <PlaceholderImage />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {propertyName && (
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#0F172A',
                  }}
                >
                  {propertyName}
                </Typography>
              )}
              {(checkInDate || checkOutDate) && (
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '12px',
                    color: '#8E98A1',
                  }}
                >
                  {checkInDate} — {checkOutDate}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Date input */}
        <CustomDatePicker
          views={['day']}
          name="extensionDate"
          label={t('TaskManagemnt.enterNewCheckoutDate')}
          format="YYYY-MM-DD"
          minDate={checkOutDate ? dayjs(checkOutDate) : undefined}
          withOutErrorMessage
        />

        {/* Dynamic Fields based on Platform SRC */}
        {selectedPropertyId !== null && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {platformSrc === 6 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: '16px',
                  borderRadius: '8px',
                  border: '1px solid #E8E8EA',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#0F172A',
                    }}
                  >
                    {t('MANUALRESERVATION.DIRECT_PAYMENT_RECEIPT')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '12px',
                      color: '#8E98A1',
                    }}
                  >
                    {t('MANUALRESERVATION.PAYMENT_LINK_DESCRIPTION')}
                  </Typography>
                </Box>
                <Toggle
                  checked={methods.watch('isDirectPaymentAccepted')}
                  onChange={(_event: any, checked: boolean) => methods.setValue('isDirectPaymentAccepted', checked)}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: '16px',
                    borderRadius: '8px',
                    border: '1px solid #E8E8EA',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#0F172A',
                      }}
                    >
                      {t('MANUALRESERVATION.DIRECT_PAYMENT_RECEIPT')}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '12px',
                        color: '#8E98A1',
                      }}
                    >
                      {t('MANUALRESERVATION.PAYMENT_LINK_DESCRIPTION')}
                    </Typography>
                  </Box>
                  <Toggle
                    checked={methods.watch('isDirectPaymentAccepted')}
                    onChange={(_event: any, checked: boolean) => methods.setValue('isDirectPaymentAccepted', checked)}
                  />
                </Box>
                <Input
                  name="guestEmail"
                  label={t('MANUALRESERVATION.GUEST_EMAIL')}
                  placeholder={t('MANUALRESERVATION.GUEST_EMAIL')}
                  registerOptions={{
                    required: {
                      value: true,
                      message: t('MANUALRESERVATION.PLEASEENTERTHEGUESTSEMAIL'),
                    },
                    pattern: {
                      value: FORM_REGEX_VALIDATORS.email,
                      message: t('MANUALRESERVATION.PLEASEENTERTHEGUESTSEMAILCORRECTLY'),
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Label */}
        <Typography
          sx={{
            fontFamily: 'Tajawal, sans-serif',
            fontSize: '14px',
            color: '#8E98A1',
          }}
        >
          {t('TaskManagemnt.availableProperties')}
        </Typography>

        {/* Loading skeletons */}
        {isLoadingProperties && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  p: '16px',
                  borderRadius: '8px',
                  border: '1px solid #E8E8EA',
                }}
              >
                <Skeleton variant="rounded" width={64} height={48} sx={{ flexShrink: 0, borderRadius: '8px' }} />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="80%" height={16} />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Empty state */}
        {!isLoadingProperties && availableProperties.length === 0 && extensionDate && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#FFF8F0',
              border: '1px solid #FFE0B2',
              borderRadius: '8px',
              p: '12px',
            }}
          >
            <LuAlertTriangle size={16} color="#FF8C00" style={{ flexShrink: 0 }} />
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontSize: '13px',
                color: '#B45309',
                flex: 1,
              }}
            >
              {t('TaskManagemnt.noAvailableProperties')}
            </Typography>
          </Box>
        )}

        {!isLoadingProperties &&
          availableProperties.map((property) => {
            const isSelected = selectedPropertyId === property.propertyId;
            return (
              <Box
                key={property.propertyId}
                onClick={() => onSelectProperty(property.propertyId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  p: '16px',
                  borderRadius: '8px',
                  border: isSelected ? '2px solid #5B3FFF' : '1px solid #E8E8EA',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#F9F8FF' : '#FFFFFF',
                  boxShadow: '0px 0px 1px 0px rgba(12,26,75,0.1), 0px 4px 20px 0px rgba(50,50,71,0.08)',
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: '#5B3FFF' },
                }}
              >
                {/* Property image */}
                <Box
                  sx={{
                    width: '64px',
                    height: '48px',
                    borderRadius: '8px',
                    backgroundColor: '#E8E8EA',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {property.propertyImage ? (
                    <Box
                      component="img"
                      src={property.propertyImage}
                      alt={property.propertyName}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <PlaceholderImage />
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#0F172A',
                    }}
                  >
                    {isRtl ? (property.propertyNameAr || property.propertyName) : property.propertyName}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '12px',
                      color: '#8E98A1',
                    }}
                  >
                    {property.location || property.city || ''}
                  </Typography>
                </Box>
              </Box>
            );
          })}
      </Box>
    </FormProvider>
  );
};

export default ExtendWithMoveTab;
